import { Outlet, Link } from "react-router-dom";
import logo from "../../../assets/images/cloudVantage Logo.png";
import { useContext, useState, useEffect } from "react";
import { ThemeContext } from "../../../components/themeContext";
import { motion, AnimatePresence } from "framer-motion";

const portalFeatures = [
  {
    title: "Give Learners Real Cloud Experience",
    description: "Empower students with direct, hands-on access to enterprise-grade cloud infrastructure and professional IT environments.",
    icon: "☁️"
  },
  {
    title: "Next-Gen Digital Classroom",
    description: "A comprehensive Learning Management System (LMS) to unify resources, e-learning, and student evaluations in one place.",
    icon: "🎯"
  },
  {
    title: "Bridge the Practical Gap",
    description: "Prepare learners for the global tech workforce by moving beyond theory into real-world cloud labs and infrastructure tasks.",
    icon: "💎"
  },
  {
    title: "Scalable School Infrastructure",
    description: "Africa's premier IaaS provider for educational institutions, delivering secure and high-performance hosting for everyone.",
    icon: "🏢"
  }
];

const AuthLayout = () => {
  const { theme } = useContext(ThemeContext);
  const [featureIndex, setFeatureIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setFeatureIndex((prev) => (prev + 1) % portalFeatures.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="h-screen flex flex-col md:flex-row bg-white dark:bg-[#141D26] overflow-hidden">
      {/* Left side - Branding & Dynamic Visuals - FIXED */}
      <div className="hidden md:flex md:w-1/2 lg:w-[45%] h-full bg-gradient-to-br from-[#1da1fc] via-[#3b82f6] to-[#60a5fa] p-12 lg:p-20 flex-col justify-between relative overflow-hidden shrink-0">
        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        {/* Animated Background Circles */}
        <motion.div 
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-white rounded-full opacity-10 blur-3xl"
        ></motion.div>

        <div className="relative z-10">


          <div className="mt-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={featureIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="text-white"
              >
                <div className="text-5xl mb-10 flex items-center justify-center w-24 h-24 bg-white/20 rounded-3xl backdrop-blur-md shadow-2xl border border-white/30">
                  {portalFeatures[featureIndex].icon}
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  {portalFeatures[featureIndex].title}
                </h2>
                <p className="text-lg lg:text-xl opacity-90 max-w-sm leading-relaxed">
                  {portalFeatures[featureIndex].description}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Feature Index Indicators */}
        <div className="relative z-10">
          <p className="text-white/50 text-[10px] uppercase tracking-widest mb-4 font-bold">Solutions Portfolio</p>
          <div className="flex gap-2.5">
            {portalFeatures.map((_, i) => (
              <button 
                key={i} 
                onClick={() => setFeatureIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-500 hover:bg-white/50 ${i === featureIndex ? 'w-12 bg-white' : 'w-2.5 bg-white/20'}`}
                aria-label={`Go to slide ${i + 1}`}
              ></button>
            ))}
          </div>
        </div>

        {/* Abstract Graphic Element at bottom */}
        <div className="absolute bottom-[-50px] left-[-20px] w-[300px] h-[300px] border-[40px] border-white/10 rounded-full"></div>
      </div>

      {/* Right side - Form Content - SCROLLABLE */}
      <div className="flex-1 h-full overflow-y-auto bg-gray-50/20 dark:bg-[#141D26]">
        <div className="min-h-full flex flex-col items-center justify-center p-6 md:p-12 lg:p-20 relative">
          <div className="w-full max-w-md my-auto flex flex-col">
            {/* Logo area */}
            <div className="flex justify-center mb-10">
              <Link to="/">
                <img 
                  src={logo} 
                  alt="CloudVantage Logo" 
                  className={`h-10 object-contain ${theme === 'dark' ? 'brightness-200' : ''}`} 
                />
              </Link>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex-1"
            >
              <Outlet />
            </motion.div>
          </div>

          {/* Footer for Right Side */}
          <div className="mt-auto pt-16 text-center text-[10px] uppercase tracking-widest text-gray-400 font-bold opacity-70">
            &copy; {new Date().getFullYear()} CloudVantage Schools Portal. African Technology Powered.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
